import BigNumber from "bignumber.js";
import { omit } from "lodash";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";

import { FeatureCode } from "@wallet-manager/node-types";
import {
  OperationBatchStatus,
  SwapExecutionType,
} from "@wallet-manager/node-types/dist/src/postgres/const";

import {
  getSwapRequestRecord,
  getSwapRequestRecordExport,
} from "../../../api/merchant";
import {
  IgetWithdrawRequestParams,
  IoperationBatchStatus,
  Ipage,
} from "../../../api/types";
import { ChainSingleSelection } from "../../../components/ChainSelection";
import { useDatePicker } from "../../../components/DatePicker";
import { useAssets, useChains } from "../../../components/FetchConfig";
import GeneralBtn from "../../../components/GeneralBtn";
import { MultiSelection } from "../../../components/GeneralSelection";
import LoadingDialog from "../../../components/LoadingDialog";
import MpTextField from "../../../components/MpTextField";
import {
  Box,
  Container,
  InputAdornment,
  SelectChangeEvent,
} from "../../../components/MuiGenerals";
import {
  useAlerting,
  usePermission,
  useTabs,
  useTranslation,
} from "../../../hooks";
import {
  enumNewOperationBatchStatus,
  tableConfig,
} from "../../../utils/constant";
import {
  displayAmount,
  downloadFiles,
  findChainInfo,
  getDecimal,
  getFullApiResponse,
  isOperationSmartAccount,
  listMappingTransform,
  separateBatchStrings,
  toDisplayTime,
} from "../../../utils/helper";
import { DivideLine, genField } from "../../../utils/HelperComp";
import { customSx } from "../../../utils/styling";
import { createZusInstance } from "../../../zustand/store";
import CurrencyPairSingleSelection from "../Swap/components/CurrencyPairSingleSelection";
import SwapRequestRecordList from "./SwapRequestRecordList";

const translatePrefix = "swapRequestRecord";

const clearApiParams = (params: Record<string, any>) => {
  return Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) =>
        value !== "" &&
        value !== null &&
        value !== undefined &&
        !(Array.isArray(value) && value.length === 0),
    ),
  );
};

function SwapRequestRecord() {
  const { tc } = useTranslation(translatePrefix);

  const tabsArr = [{ name: tc("table"), value: "" }];

  const { Tabs } = useTabs(tabsArr);

  return (
    <Box sx={customSx.layoutBox}>
      <Container disableGutters maxWidth={false}>
        <FilterBar />
      </Container>
      <DivideLine />
      <Container
        style={customSx.datagridContainer}
        maxWidth={false}
        disableGutters
      >
        <Tabs>
          <SwapRequestRecordList listMapping={listMapping} />
        </Tabs>
      </Container>
    </Box>
  );
}

export default SwapRequestRecord;

interface Ifields {
  chain_name: string;
  status: IoperationBatchStatus[];
  directions: string[];
  currency_pair: string;
  batchIdsString: string;
  merchantIdsString: string;
}
const initFields = {
  chain_name: "",
  status: [] as IoperationBatchStatus[],
  directions: [],
  currency_pair: "",
  batchIdsString: "",
  merchantIdsString: "",
};

const initZusParams = {
  createdDateFrom: "",
  createdDateTo: "",
  batch_id: [] as string[],
  merchant_order_id: [] as string[],
  page: 0,
  pageSize: tableConfig.pageSize,
  ...omit(initFields, ["batchIdsString", "merchantIdsString", "currency_pair"]),
};
export const useZusParams = createZusInstance<
  IgetWithdrawRequestParams & Ipage
>(initZusParams);
function FilterBar() {
  const DateObj = {
    Created: useDatePicker(),
    Modified: useDatePicker(),
  };
  const { hasPermission } = usePermission();
  const [fields, setFields] = useState<Ifields>(initFields);
  const { selectAssetNamesByChain } = useAssets();
  const { selectChainByPieces } = useChains();
  const asset_names = selectAssetNamesByChain({
    chain_name: fields.chain_name,
  });
  const { alerting } = useAlerting();
  const { t, te, tc } = useTranslation(translatePrefix);
  const zusParams = useZusParams();
  const setAssets = (asset_names: string[]) =>
    setFields((fields) => ({ ...fields, asset_names }));
  useEffect(() => setAssets([]), [fields.chain_name]);
  const onChange =
    (type: keyof Ifields) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value;
      setFields((fields) => ({ ...fields, [type]: value }));
    };

  const currencyPairAllDirections = getDirections();

  function getParams() {
    const {
      batchIdsString,
      merchantIdsString,
      chain_name,
      directions,
      currency_pair,
      ...rest
    } = fields;
    const { chain_id, chain_type } = selectChainByPieces({ chain_name });

    const fromAssetName =
      directions.length === 1 ? directions[0]?.split("/")[0] : undefined;
    const toAssetName =
      directions.length === 1 ? directions[0]?.split("/")[1] : undefined;

    return {
      ...rest,
      batch_id: separateBatchStrings(fields.batchIdsString),
      merchant_order_id: separateBatchStrings(fields.merchantIdsString),
      chain_id,
      chain_type: String(chain_type || ""),
      symbol: currency_pair ? [currency_pair] : undefined,
      fromAssetName,
      toAssetName,
      created_date_from: DateObj.Created.start,
      created_date_to: DateObj.Created.end,
      last_modified_date_from: DateObj.Modified.start,
      last_modified_date_to: DateObj.Modified.end,
      page: 0,
    };
  }
  const apiParams = getParams();
  const onSearch = () => {
    zusParams.setBody(apiParams);
    zusParams.refetch();
  };
  const onReset = () => {
    setFields(initFields);
    DateObj.Created.clearDate();
    DateObj.Modified.clearDate();
  };
  const listRes = useQuery({
    queryKey: [
      "getSwapRequestRecord",
      zusParams.body,
      zusParams.refetchCounter,
    ],
    queryFn: () => {
      const cleanParams = clearApiParams(zusParams.body);
      return getSwapRequestRecord(cleanParams);
    }, // TODO: change to correct api
  });
  const onExport = async () => {
    const count = listRes.data?.count || 0;
    if (count === 0) {
      return alerting("error", tc("no_data_export"));
    }
    const apiFn = (page: number, pageSize: number, signal: any) =>
      getSwapRequestRecordExport(
        { ...clearApiParams(apiParams), page, pageSize },
        { signal },
      );
    const rawRes = await getFullApiResponse(apiFn, count, true);
    if (rawRes.length === 0) return;
    const res = listMapping("name", rawRes, { t, te, omitKeys: ["rawData"] });
    const config = {};
    downloadFiles(`Swap Request Records`, res, config);
  };

  function getDirections() {
    if (!fields.currency_pair) {
      return {};
    }

    const splittedCurrencyPair = fields.currency_pair.split("/");

    const reversedSplittedCurrencyPair = [...splittedCurrencyPair].reverse();

    const getDisplayValue = (splittedCurrencyPair: string[]) => {
      return splittedCurrencyPair.join(" → ");
    };

    const getDataValue = (splittedCurrencyPair: string[]) => {
      return splittedCurrencyPair.join("/");
    };

    const displayValue = getDisplayValue(splittedCurrencyPair);
    const reversedDisplayValue = getDisplayValue(reversedSplittedCurrencyPair);

    return {
      [displayValue]: getDataValue(splittedCurrencyPair),
      [reversedDisplayValue]: getDataValue(reversedSplittedCurrencyPair),
    };
  }

  const onArraySelectChange =
    (key: keyof Ifields) => (e: SelectChangeEvent<string[]>) => {
      const { value } = e.target;
      const newValue = typeof value === "string" ? value.split(",") : value;
      return setFields((fields) => ({ ...fields, [key]: newValue }));
    };
  const onFilterChange =
    (type: keyof Ifields) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value;
      setFields((fields) => ({ ...fields, [type]: value }));
    };
  const F = genField({ t }, [
    ["creation_time", <DateObj.Created.Picker type="dateTime" />],
    ["last_modified_time", <DateObj.Modified.Picker type="dateTime" />],
    [
      "chain_name",
      <ChainSingleSelection
        label={t("ph_chain_name")}
        setChoice={(chain_name) =>
          setFields((f) => ({
            ...f,
            chain_name,
            currency_pair: "",
            directions: [],
          }))
        }
        choice={fields.chain_name}
        onlyEthAndTron
      />,
    ],
    [
      "currency_pair",
      <CurrencyPairSingleSelection
        label={t("ph_currency_pair")}
        value={fields.currency_pair}
        chainName={fields.chain_name}
        onChange={function (e: SelectChangeEvent): void {
          setFields((fields) => ({
            ...fields,
            currency_pair: e.target.value,
            directions: [],
          }));
        }}
        onClear={() =>
          setFields((fields) => ({
            ...fields,
            currency_pair: "",
            directions: [],
          }))
        }
      />,
    ],
    [
      "status",
      <MultiSelection
        label={t("ph_status")}
        value={fields.status}
        onChange={onArraySelectChange("status")}
        enumData={enumNewOperationBatchStatus}
      />,
    ],
    [
      "direction",
      <MultiSelection
        label={t("ph_direction")}
        value={fields.directions}
        onChange={onArraySelectChange("directions")}
        nameFn={(name) => name}
        enumData={currencyPairAllDirections}
      />,
    ],
    [
      "batch_id",
      <MpTextField
        label={t("ph_batch_id")}
        value={fields.batchIdsString}
        onChange={onChange("batchIdsString")}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {apiParams.batch_id.length}
            </InputAdornment>
          ),
        }}
      />,
    ],
    [
      "merchant_order_id",
      <MpTextField
        label={t("ph_merchant_order_id")}
        value={fields.merchantIdsString}
        onChange={onFilterChange("merchantIdsString")}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {apiParams.merchant_order_id.length}
            </InputAdornment>
          ),
        }}
      />,
    ],
  ]);
  if (listRes.isLoading) return <LoadingDialog forceOpen={true} />;
  return (
    <>
      <Box sx={customSx.filterCmdBar}>
        <GeneralBtn
          label="export"
          isHidden={
            !hasPermission(
              FeatureCode.FeaturesAssetManagement.SwapRequestRecord.Export,
            )
          }
          onClick={onExport}
        />
      </Box>
      <Box sx={customSx.gridFilter} className="gridFilter">
        {F.creation_time}
        {F.last_modified_time}
        {F.chain_name}
        {F.currency_pair}
        {F.status}
        {F.direction}
        {F.batch_id}
        {F.merchant_order_id}
      </Box>
      <Box sx={customSx.filterB}>
        <GeneralBtn label="search" onClick={onSearch} />
        <GeneralBtn label="reset" onClick={onReset} />
      </Box>
    </>
  );
}

const listMapping = (
  which: "key" | "name",
  array: any[],
  config: { t: any; te: any; omitKeys?: string[] },
): any[][] => {
  const { t, te, omitKeys = [] } = config;
  const res = array.map((item: ApiRes) => {
    const decimal = getDecimal(item);
    // const minerFeeDecimal = getDecimal({
    //   chain_type: item.batch.chain_type,
    //   chain_id: item.batch.chain_id,
    //   asset_name: item.batch.miner_fee_asset_name,
    // });

    const firstSwapOperationItem: Swapoperation | undefined =
      item.swap_operations?.[0];

    const swapExecutionType = item.execution_type;

    const toAmount = (amount: number | string, dec: string = decimal) =>
      displayAmount(amount, dec);

    const getMaxSlippage = () => {
      if (item.max_slippage) {
        return `${(Number(item.max_slippage) / 100000) * 100}%`;
      }

      return "";
    };

    const calculateTotal = (
      key:
        | "from_amount"
        | "from_amount_actual"
        | "to_amount"
        | "to_amount_actual",
    ) => {
      return item.swap_operations
        ?.reduce((acc, cur) => {
          return acc.plus(cur[key] || 0);
        }, BigNumber(0))
        .toFixed()
        .toString();
    };

    const getTotalAmount = (direction: "from" | "to") => {
      if (item[`${direction}_amount_total`]) {
        return toAmount(
          item[`${direction}_amount_total`],
          String(item[`${direction}_asset_decimals`]),
        );
      }

      return "";
    };

    const getActualAmount = (direction: "from" | "to") => {
      const displayValueStatuses = [
        OperationBatchStatus.Completed,
        OperationBatchStatus.PartialCompleted,
      ];
      const displayZeroStatuses = [
        OperationBatchStatus.SignFailed,
        OperationBatchStatus.Rejected,
        OperationBatchStatus.Failed,
      ];

      const operationBatchStatus = item.wallet_operation_batch?.status;

      if (displayZeroStatuses.includes(operationBatchStatus)) {
        return "0";
      }

      if (
        item[`${direction}_amount_total_actual`] &&
        displayValueStatuses.includes(operationBatchStatus)
      ) {
        return toAmount(
          item[`${direction}_amount_total_actual`],
          String(item[`${direction}_asset_decimals`]),
        );
      }

      return "";
    };

    const mappedResult = [
      ["batch_id", t("batch_id"), item.batch_id],
      [
        "chain_name",
        t("chain_name"),
        findChainInfo(String(item.chain_type), item.chain_id)?.name,
      ],
      [
        "smart_account_type",
        t("smart_account_type"),
        isOperationSmartAccount(item.wallet_operation_batch?.smart_account_type)
          ? te("yes")
          : te("no"),
      ],
      [
        "wallet_address",
        t("wallet_address"),
        firstSwapOperationItem.from_address,
      ],
      [
        "status",
        t("status"),
        te(OperationBatchStatus[item.wallet_operation_batch?.status]),
      ],
      ["max_slippage", t("max_slippage"), getMaxSlippage()],
      // ["swap_path", t("swap_path"), item.swap_path],
      [
        "swap_execution_type",
        t("swap_execution_type"),
        te(SwapExecutionType[swapExecutionType]),
      ],
      ["currency_from", t("currency_from"), item.from_asset_name],
      ["total_amount_from", t("total_amount_from"), getTotalAmount("from")],
      ["actual_amount_from", t("actual_amount_from"), getActualAmount("from")],
      ["currency_to", t("currency_to"), item.to_asset_name],
      ["total_amount_to", t("total_amount_to"), getTotalAmount("to")],
      ["actual_amount_to", t("actual_amount_to"), getActualAmount("to")],
      // ["actual_amount", t("actual_amount"), getActualAmount()],
      ["total_count", t("total_count"), item.total_count],
      // ["exchange_rate", t("exchange_rate"), item.exchange_rate],
      // [
      //   "estimate_get_amount",
      //   t("estimate_get_amount"),
      //   toAmount(item.estimate_get_amount),
      // ],
      // ["minimum_get_amount", t("minimum_get_amount"), item.min_get],
      // ["exact_get_amount", t("exact_get_amount"), item.exact_get_amount],
      // [
      //   "estimate_pay_amount",
      //   t("estimate_pay_amount"),
      //   toAmount(item.estimate_pay_amount),
      // ],
      // ["maximum_pay_amount", t("maximum_pay_amount"), item.max_pay],
      // ["exact_pay_amount", t("exact_pay_amount"), item.exact_pay_amount],
      // ["quoted_time", t("quoted_time"), item.quoted_time],
      ["created_by", t("created_by"), item.wallet_operation_batch?.created_by],
      ["creation_time", t("creation_time"), toDisplayTime(item.created_date)],
      [
        "last_modified_time",
        t("last_modified_time"),
        toDisplayTime(item.last_modified_date),
      ],
      ["rawData", "", item],
    ].filter(([key]) => !omitKeys.includes(key));
    return mappedResult;
  });
  const output = res.map(listMappingTransform(which));
  return output;
};

type ApiRes = {
  id: string;
  merchant_id: string;
  batch_id: string;
  swap_type: number;
  chain_type: number;
  chain_id: string;
  execution_type: number;
  symbol: string;
  max_slippage: string;
  from_asset_name: string;
  to_asset_name: string;
  swap_path: string[];
  from_amount_total: string;
  from_amount_total_actual: string;
  from_asset_decimals: number;
  to_amount_total: string;
  to_amount_total_actual: string;
  to_asset_decimals: number;
  total_count: number;
  client_data: string;
  created_date: string;
  last_modified_date: string;
  swap_operations: Swapoperation[];
  wallet_operation_batch: Walletoperationbatch;
};

interface Walletoperationbatch {
  id: string;
  merchant_id: string;
  operation_type: number;
  total_operation: number;
  total_step: number;
  current_step: number;
  status: number;
  chain_type: number;
  chain_id: string;
  created_date: string;
  last_modified_date: string;
  created_by: string;
  creator_type: number;
  data_signature: string;
  data_signature_version: number;
  data_signer_server_type: number;
  data_signer_server_id: string;
  miner_fee_asset_name: null;
  miner_fee: null;
  actual_amount: null;
  latest_aggregated_transfer_id: null;
  smart_account_type: number;
}

interface Swapoperation {
  id: string;
  merchant_id: string;
  batch_id: string;
  operation_seq: number;
  merchant_order_id: string;
  execution_type: number;
  symbol: string;
  max_slippage: string;
  from_address: string;
  from_asset_name: string;
  from_asset_decimals: number;
  from_amount: string;
  from_amount_max: null;
  from_amount_estimate: null;
  from_amount_actual: null;
  to_address: string;
  to_asset_name: string;
  to_asset_decimals: number;
  to_amount: null;
  to_amount_min: string;
  to_amount_estimate: string;
  to_amount_actual: null;
  swap_path: string[];
  created_date: string;
  last_modified_date: string;
}
